import { memo, useEffect, useMemo, useRef, useState } from 'react'

import { useIncidentDeepLinkDisplayId } from '../shared/hooks/useIncidentDeepLinkDisplayId'
import { useIncidentDrawerNavigation } from '../shared/hooks/useIncidentDrawerNavigation'
import { useIncidentExtendedClipStatus } from '../shared/hooks/useIncidentExtendedClipStatus'
import { useIncidentWorkflow } from '../shared/hooks/useIncidentWorkflow'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  Box,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
} from '@chakra-ui/react'

import { Drawer } from '@/components/overlays'
import { useIncidentQuery } from '@/graphql/generated/hooks'
import { DeviceStatus, IncidentStatus } from '@/graphql/generated/schemas'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { selectIncident, selectWorkflowDrawerIsOpen } from '@/redux/ui/uiSlice'
import { mixpanel } from '@/utils/analytics'

import { IncidentLocationInfo } from '../components/IncidentLocationInfo'
import { DarkFeed, Feed } from '../feed'
import { type IncidentContextI } from '../shared/types/incidentContextTypes'
import { MixpanelDataIProps } from '../shared/types/types'
import { buildIncidentContext } from '../shared/utils/buildIncidentContext'
import { isAccessDeniedCode } from '../shared/utils/helpers'
import { SOPIncidentDrawer } from '../sop-incident-drawer/components/SOPIncidentDrawer'
import { TraceLogDrawer } from '../trace-log-drawer'
import { WorkflowDrawer } from '../workflow-drawer'
import { DarkHeader } from './DarkHeader'
import { DarkIncidentActions } from './DarkIncidentActions'
import { DrawerMessageContainer } from './DrawerMessageContainer'
import { IncidentActions } from './IncidentActions'
import { IncidentDrawerHeader } from './IncidentDrawerHeader'
import { IncidentMedia } from './IncidentMedia'

export interface IncidentDrawerIProps {
  isOpen: boolean
  onClose: () => void
}

export const IncidentDrawer = memo(
  ({ isOpen, onClose }: IncidentDrawerIProps) => {
    const { t } = useTranslation()
    const incidentId = useAppSelector(selectIncident)?.id
    const me = useAppSelector(selectMe)
    const urlDisplayId = useIncidentDeepLinkDisplayId()
    const bodyRef = useRef<HTMLDivElement>()
    const [isAccessDenied, setIsAccessDenied] = useState(false)
    const {
      traceLogs: isTraceLogsEnabled,
      resolvedIncidentClip: isResolvedClipEnabled,
      incidentDrawerRebrand: isIncidentDrawerRebrand,
    } = useFeatureFlags()

    const {
      data,
      loading: isIncidentsLoading,
      refetch: refetchFeedItems,
      error,
    } = useIncidentQuery({
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      pollInterval: isAccessDenied ? 0 : 3000,
      variables: {
        id: incidentId ?? '',
      },
    })

    const rawIncident = data?.incident
    // Cache race: Apollo can briefly return previous-incident data on incident switches.
    // Treat displayId mismatch (URL flow) or id mismatch (row click / nav) as loading.
    const isAwaitingRequestedIncident =
      !!urlDisplayId && !!rawIncident && rawIncident.displayId !== urlDisplayId
    const isStaleIncident = !!rawIncident && rawIncident.id !== incidentId
    const incident =
      isAwaitingRequestedIncident || isStaleIncident ? undefined : rawIncident
    const isLoading =
      (isIncidentsLoading && (!data || isStaleIncident)) ||
      isAwaitingRequestedIncident
    const device = incident?.devices?.[0]
    const isArchived = incident?.facility?.archived
    const shouldShowIncidentActions = !(isLoading || isArchived)
    // A failed fetch with nothing to render gets an error state, not a
    // permanent skeleton (ENG-33742). Gated on the raw query loading, not the
    // derived isLoading, so a deep-link displayId mismatch can't suppress the
    // error forever. Polling retries clear it on success. Access-denied codes
    // are excluded so the error message never paints on the frame before the
    // effect below flips isAccessDenied.
    const isErrored =
      !!error && !isAccessDeniedCode(error) && !incident && !isIncidentsLoading

    // Probe for the incident's EXTENDED (forensic) clip to populate `has_extended_clip` on the
    // drawer-open event. Encapsulated in a boolean-returning hook so the heavy clips query type
    // stays out of this component. Skipped when the feature flag is off, so the drawer-open event
    // is unchanged with the flag disabled.
    const { hasExtendedClip, isReady: isExtendedClipStatusReady } =
      useIncidentExtendedClipStatus(
        incidentId,
        !isOpen || !isResolvedClipEnabled
      )
    const mode = useLightMode()
    const isDark = mode === 'dark'
    const drawerBg =
      isDark && isIncidentDrawerRebrand
        ? 'var(--hw-dark-colors-bg-card, #00121b)'
        : '#ffffff'

    const {
      hasWorkflows,
      isWorkflowFeatureEnabled,
      workflowStatus,
      pendingWorkflow,
      pendingWorkflowFull,
      assignedWorkflow,
      workflowActions,
      currentStepId,
      isLoadingWorkflow,
    } = useIncidentWorkflow({ incident })

    const isActiveOwner =
      incident?.owner?.id === me?.id &&
      incident?.status === IncidentStatus.InProgress

    const workflowReportData = useMemo(
      () => ({
        assignedWorkflow,
        hasWorkflows,
        pendingWorkflowFull,
        workflowActions,
      }),
      [assignedWorkflow, hasWorkflows, pendingWorkflowFull, workflowActions]
    )

    const isWorkflowDrawerOpen = useAppSelector(selectWorkflowDrawerIsOpen)

    const {
      showNavigation: shouldShowNavigation,
      canNavigatePrevious,
      canNavigateNext,
      hasTrackedDrawerView,
      navigateToPreviousIncident,
      navigateToNextIncident,
    } = useIncidentDrawerNavigation({ isOpen, isWorkflowDrawerOpen })

    const scrollToTop = () => {
      bodyRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }

    const sopIncidentContext: IncidentContextI | null = useMemo(
      () => (incident ? buildIncidentContext(incident, device) : null),
      // incident and device properties don't change independently of the
      // incident itself, so id is sufficient as the sole dep
      [incident?.id]
    )

    const mixpanelData: MixpanelDataIProps = useMemo(() => {
      if (data) {
        return {
          incident_id: incidentId,
          incident_source: device?.type,
          incident_type: incident?.type,
          incident_status: incident?.status,
          device_id: device?.id,
          device_type: device?.type,
          facility_id: incident?.facility?.id,
          floor_id: incident?.floor?.id,
          floor_name: incident?.floor?.name,
          streaming_type: device?.camera?.streamingType,
        }
      }
    }, [incidentId, data, incident?.type, device])

    useEffect(() => {
      // Guard against tracking with stale data during navigation:
      // - Wait for loading to complete to ensure fresh data
      // - Verify incident data matches the current incidentId to avoid cache-and-network race conditions
      // - Wait for the extended-clip probe to settle so `has_extended_clip` is a real "available at
      //   open" answer (no-op when the flag is off, since the probe is skipped → ready immediately)
      if (
        !isOpen ||
        !data ||
        isIncidentsLoading ||
        incident?.id !== incidentId ||
        !isExtendedClipStatusReady ||
        hasTrackedDrawerView.current
      ) {
        return
      }

      hasTrackedDrawerView.current = true
      const date = new Date().toUTCString()
      mixpanel.track('Active Incident View, Drawer View', {
        user_id: me?.id,
        incident_id: incident?.id,
        event_at: date,
        incident_source: device?.type,
        incident_type: incident?.type,
        // NOTE! There's already an incident_source entry, but it's pointed to Device's Type. Ideally we'd have
        // that as this property's name, but I'm not sure that we should switch now as it'd muddy the historical data
        creation_method: incident?.source,
        device_id: device?.id,
        device_type: device?.type,
        facility_id: incident?.facility?.id,
        // Clips can come in after drawer open, but we're only looking to see
        has_clip_on_open: !!incident?.video?.url,
        // Whether an EXTENDED (forensic) clip was already available at open. Only recorded when the
        // resolvedIncidentClip feature is on AND availability was actually determined; omitted
        // (undefined) when the flag is off or the probe could not determine availability (error),
        // so a query failure is never reported as a misleading `false`.
        has_extended_clip:
          isResolvedClipEnabled && hasExtendedClip !== undefined
            ? hasExtendedClip
            : undefined,
        // Whether the incident's device has any tailgate association (a linked
        // camera that supplies clips) — i.e. whether we should expect a clip at all.
        is_device_associated: !!device?.tailgateConns?.totalCount,
        seconds_since_created: incident?.createdAt
          ? Math.floor((Date.now() - Date.parse(incident.createdAt)) / 1000)
          : undefined,
      })
      // `hasExtendedClip` and `isResolvedClipEnabled` are read in the body but intentionally left
      // out of the deps. This is a fire-once drawer-open tracker: `hasExtendedClip` can flip
      // false→true mid-view as clips are re-polled, and it must not re-trigger tracking, while
      // `isResolvedClipEnabled` is stable for the drawer's lifecycle. The effect still fires at the
      // right moment via `isExtendedClipStatusReady`, at which point both hold their settled values.
    }, [
      isOpen,
      incidentId,
      data,
      isIncidentsLoading,
      incident?.id,
      incident?.type,
      device,
      isExtendedClipStatusReady,
    ])

    // Check if Access is denied for incident query
    useEffect(() => {
      if (error) {
        const isAccessDenied = isAccessDeniedCode(error)
        setIsAccessDenied(isAccessDenied)
      }
    }, [error])

    return (
      <Drawer
        // autoFocus is disabled so keyboard focus stays on the table,
        // allowing ArrowUp/ArrowDown navigation between incidents to work.
        autoFocus={false}
        isOpen={isOpen}
        onClose={onClose}
        placement='right'
        size='lg'
      >
        <DrawerOverlay />
        <DrawerContent bgColor={drawerBg} boxShadow='xl'>
          <SOPIncidentDrawer incidentContext={sopIncidentContext} />
          {isTraceLogsEnabled && <TraceLogDrawer incident={incident} />}
          <WorkflowDrawer
            assignedWorkflow={assignedWorkflow}
            currentStepId={currentStepId}
            hasWorkflows={hasWorkflows}
            incidentId={incidentId ?? null}
            isActiveOwner={isActiveOwner}
            isLoadingWorkflow={isLoadingWorkflow}
            pendingWorkflow={pendingWorkflow}
            pendingWorkflowFull={pendingWorkflowFull}
            workflowActions={workflowActions}
            workflowStatus={workflowStatus}
          />
          <DrawerHeader
            bgColor={drawerBg}
            borderBottom={
              isIncidentDrawerRebrand ? 'none' : '1px solid #D5DCE4'
            }
            p={0}
            pr={isIncidentDrawerRebrand ? '0' : '24px'}
          >
            {isIncidentDrawerRebrand ? (
              <DarkHeader
                canNavigateNext={canNavigateNext}
                canNavigatePrevious={canNavigatePrevious}
                colorScheme={isDark ? 'dark' : 'light'}
                deviceIsUnhealthy={device?.status === DeviceStatus.Unhealthy}
                incident={incident}
                isLoading={isLoading}
                onClose={onClose}
                onNavigateNext={() => navigateToNextIncident('click')}
                onNavigatePrevious={() => navigateToPreviousIncident('click')}
                showNavigation={shouldShowNavigation}
              />
            ) : (
              <IncidentDrawerHeader
                canNavigateNext={canNavigateNext}
                canNavigatePrevious={canNavigatePrevious}
                incident={incident}
                isLoading={isLoading}
                onClose={onClose}
                onNavigateNext={() => navigateToNextIncident('click')}
                onNavigatePrevious={() => navigateToPreviousIncident('click')}
                showNavigation={shouldShowNavigation}
              />
            )}
          </DrawerHeader>
          {isAccessDenied ? (
            <DrawerMessageContainer
              message={t('incidents:incident_drawer_access_denied_message')}
              title={t('incidents:incident_drawer_access_denied_title')}
            />
          ) : isErrored ? (
            <DrawerMessageContainer
              message={t('incidents:incident_drawer_error_message')}
              title={t('incidents:incident_drawer_error_title')}
            />
          ) : (
            <DrawerBody bgColor={drawerBg} p={0} ref={bodyRef}>
              {/*
                IncidentMedia must be a direct child of DrawerBody: it renders sticky elements
                (the "Camera view" bar) whose sticky range is their containing block. A wrapper
                box clamps them to the media section, so the bar releases as soon as the feed
                scrolls into view — and toggling it resizes the wrapper, bouncing the player in
                a jumping loop (ENG-31993). The rebrand gap below the media is a spacer instead.
              */}
              <IncidentMedia
                incident={incident}
                isLoading={isLoading}
                mixpanelData={mixpanelData}
                scrollToTop={scrollToTop}
                useViewToolbar
              />
              {isIncidentDrawerRebrand && <Box h='24px' />}

              {!isIncidentDrawerRebrand && (
                <Box marginTop={4} marginX={4}>
                  <IncidentLocationInfo
                    incident={incident}
                    isLoading={isLoading}
                    onClose={onClose}
                  />
                </Box>
              )}

              {shouldShowIncidentActions &&
                (isIncidentDrawerRebrand ? (
                  <DarkIncidentActions
                    hasWorkflows={hasWorkflows}
                    incident={incident}
                    isLoading={isLoading}
                    isLoadingWorkflow={isLoadingWorkflow}
                    isWorkflowFeatureEnabled={isWorkflowFeatureEnabled}
                    mixpanelData={mixpanelData}
                    onClose={onClose}
                    refetchFeedItems={refetchFeedItems}
                    workflowReportData={workflowReportData}
                    workflowStatus={workflowStatus}
                  />
                ) : (
                  <IncidentActions
                    hasWorkflows={hasWorkflows}
                    incident={incident}
                    isLoadingWorkflow={isLoadingWorkflow}
                    isWorkflowFeatureEnabled={isWorkflowFeatureEnabled}
                    mixpanelData={mixpanelData}
                    refetchFeedItems={refetchFeedItems}
                    workflowReportData={workflowReportData}
                    workflowStatus={workflowStatus}
                  />
                ))}
              {isIncidentDrawerRebrand ? (
                <DarkFeed
                  incident={incident}
                  isLoading={isLoading}
                  mixpanelData={mixpanelData}
                  onClose={onClose}
                />
              ) : (
                <Feed
                  incident={incident}
                  isLoading={isLoading}
                  mixpanelData={mixpanelData}
                  onClose={onClose}
                />
              )}
            </DrawerBody>
          )}
        </DrawerContent>
      </Drawer>
    )
  }
)
